import { memo } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { Markdown } from '~/components/chat/Markdown';
import { AssistantMessagePart } from '~/components/chat/assistant-message/AssistantMessagePart';
import { AssistantThinking } from '~/components/chat/assistant-message/AssistantThinking';
import type { ProcessedPart, TodoGroupsData } from '~/components/chat/assistant-message/types';
import { isBoltMessage } from '~/lib/agents';
import { isTextPart, isTodoWritePart, isToolInvocationGroupPart, isToolInvocationPart } from '~/lib/ai';
import type { AssistantMessageTodo, Message } from '~/types/chat';

interface AssistantMessageContentProps {
  markdownContent: string;
  scrollToBottom: ScrollToBottom;
  message: Message;
  processedParts: ProcessedPart[];
  todoGroupsData: TodoGroupsData;
  visibleParts: ProcessedPart[];
}

export const AssistantMessageContent = memo((props: AssistantMessageContentProps) => {
  const { markdownContent, scrollToBottom, message, processedParts, todoGroupsData, visibleParts } = props;

  const isStreaming = message.status === 'streaming';
  const lastProcessedPart = processedParts?.at(-1);
  const lastVisiblePart = visibleParts.at(-1);

  if (isBoltMessage(message)) {
    return (
      <Markdown html scrollToBottom={scrollToBottom}>
        {markdownContent}
      </Markdown>
    );
  }

  if (!lastVisiblePart) {
    return null;
  }

  const todos = isTodoWritePart(lastProcessedPart)
    ? (lastProcessedPart.toolInvocation.args.todos as AssistantMessageTodo[])
    : [];

  const hasPendingOrInProgressTodos = todos.some((todo) => ['pending', 'in_progress'].includes(todo.status));
  const lastPartIsCompletedTodoWrite = todos.length > 0 && !hasPendingOrInProgressTodos;

  const lastPartIsText = lastVisiblePart && isTextPart(lastVisiblePart);
  const lastPartIsToolCallGroup = lastProcessedPart && isToolInvocationGroupPart(lastProcessedPart);
  const lastPartIsToolCall = lastProcessedPart && isToolInvocationPart(lastProcessedPart);

  const showThinking = isStreaming && (lastPartIsText || lastPartIsCompletedTodoWrite);

  const isPending =
    isStreaming && (lastPartIsText || lastPartIsToolCallGroup || lastPartIsToolCall || lastPartIsCompletedTodoWrite);

  return (
    <div className="flex flex-col gap-4">
      {visibleParts.map((part, index) => (
        <AssistantMessagePart
          key={part.id}
          part={part}
          isPending={index === visibleParts.length - 1 && isPending}
          todoGroupsData={todoGroupsData}
          scrollToBottom={scrollToBottom}
        />
      ))}

      {showThinking && <AssistantThinking includeIcon />}
    </div>
  );
});

AssistantMessageContent.displayName = 'AssistantMessageContent';
